import { Controller } from "@hotwired/stimulus";

// Guided "Build with Oscar" wizard for funnels. One question per screen
// with big button/card choices — demo users froze at the old free-text
// brief box because they didn't know what to type. Every field lives in
// ONE form and submits together; this controller only governs which step
// is visible, the progress bar, and conditional fields that appear when a
// certain choice is made (e.g. "Something else" reveals a text box, "Pick
// a date" reveals a date picker).
//
// Reveal wiring is done at the form level: `change` events bubble up from
// any radio/checkbox to the form's `change->funnel-wizard#syncReveals`,
// so the choice components don't need to know about this controller.
export default class extends Controller {
  static targets = ["step", "counter", "progress", "back", "next", "submit", "reveal"];

  connect() {
    this.index = 0;
    this.render();
    this.syncReveals();
  }

  next(event) {
    if (event) event.preventDefault();
    if (this.index < this.lastIndex) {
      this.index += 1;
      this.render();
      this.scrollUp();
    }
  }

  back(event) {
    if (event) event.preventDefault();
    if (this.index > 0) {
      this.index -= 1;
      this.render();
      this.scrollUp();
    }
  }

  // Enter advances the wizard instead of submitting early — except on the
  // final step (where the submit button is showing) and inside textareas
  // (the "anything else" notes should wrap).
  keydown(event) {
    if (event.key !== "Enter") return;
    if (event.target.tagName === "TEXTAREA") return;
    if (this.index < this.lastIndex) {
      event.preventDefault();
      this.next();
    }
  }

  // Show/hide conditional fields based on the currently-checked choice
  // (e.g. "Something else" reveals a text box; "Pick a date" reveals the
  // date picker). We only toggle visibility, not `disabled` — a stale
  // value left in a hidden reveal is inert because GenerateContent reads
  // `offer_other`/`deadline_date` ONLY inside their controlling branch,
  // and disabling would keep the date picker from initializing.
  syncReveals() {
    this.revealTargets.forEach((el) => {
      const name = el.dataset.revealName;
      const want = el.dataset.revealValue;
      const checked = this.element.querySelector(`input[name="${name}"]:checked`);
      el.hidden = !(Boolean(checked) && checked.value === want);
    });
  }

  get lastIndex() {
    return this.stepTargets.length - 1;
  }

  render() {
    if (this.stepTargets.length === 0) return;
    this.stepTargets.forEach((el, i) => {
      el.hidden = i !== this.index;
    });
    const total = this.stepTargets.length;
    if (this.hasCounterTarget) {
      this.counterTarget.textContent = `Step ${this.index + 1} of ${total}`;
    }
    if (this.hasProgressTarget) {
      this.progressTarget.style.width = `${((this.index + 1) / total) * 100}%`;
    }
    // The nav buttons carry `.btn` (display: inline-flex), which ties the
    // `[hidden]` preflight rule on specificity and wins on source order —
    // so the `hidden` attribute alone won't hide them. Drive them with an
    // inline `display` (beats any class) instead.
    const onLast = this.index === this.lastIndex;
    if (this.hasBackTarget) this.setVisible(this.backTarget, this.index !== 0);
    if (this.hasNextTarget) this.setVisible(this.nextTarget, !onLast);
    if (this.hasSubmitTarget) this.setVisible(this.submitTarget, onLast);
  }

  setVisible(el, visible) {
    el.hidden = !visible;
    el.style.display = visible ? "" : "none";
  }

  scrollUp() {
    this.element.scrollIntoView({ behavior: "smooth", block: "start" });
  }
}
